import { Link } from 'react-router-dom';
import { Globe, Settings } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useChannels } from '../queries';
import { useIntegrationStatus } from '@/features/settings';

export function WebsiteAnalyticsPage() {
  const channels = useChannels();
  const status = useIntegrationStatus();

  const row = (channels.data ?? []).find((c) => c.source === 'website');
  // No dedicated GA / Plausible integration yet — we surface CRM-side "website"
  // lead inflow and link to the integrations settings.
  const provider = (status.data ?? []).find((p) => p.key === 'website');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Globe className="text-muted-foreground size-6" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Website</h1>
          <p className="text-muted-foreground text-sm">
            Lead inflow from the website + a placeholder for traffic analytics.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
          <div>
            <CardTitle className="text-base">Traffic analytics not connected</CardTitle>
            <CardDescription>
              GA / Plausible integration is on the roadmap (Wave 11). Lead-side data below works without it.
            </CardDescription>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/settings/integrations">
              <Settings className="size-4" /> Integrations
            </Link>
          </Button>
        </CardHeader>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader>
            <CardDescription>Website-sourced leads</CardDescription>
            <CardTitle className="text-3xl tabular-nums">
              {channels.isPending ? <Skeleton className="h-9 w-16" /> : row?.leads ?? 0}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>Provider status</CardDescription>
            <CardTitle className="text-base">
              {status.isPending ? (
                <Skeleton className="h-5 w-24" />
              ) : provider?.configured ? (
                <Badge variant="secondary">Connected</Badge>
              ) : (
                <Badge variant="outline">Not configured</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-muted-foreground text-xs">
            Adding a provider here will surface visits, bounce rate, top pages.
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
